import { getLegalDoc, type LegalDoc } from '@/legal/legal-content';

interface LegalModalProps {
  docId: LegalDoc['id'] | null;
  onClose: () => void;
}

/** Render mínimo del markdown ligero usado en los textos legales (## / - / párrafos). */
function renderBody(body: string) {
  return body.split('\n').map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} style={{ height: 8 }} />;
    if (t.startsWith('## ')) {
      return (
        <h3 key={i} style={{ fontSize: 15, fontWeight: 800, color: 'var(--navy)', margin: '16px 0 6px' }}>
          {t.slice(3)}
        </h3>
      );
    }
    if (t.startsWith('- ')) {
      return (
        <div key={i} style={{ display: 'flex', gap: 8, margin: '3px 0', fontSize: 13.5, color: 'var(--ink-500, #334)', lineHeight: 1.5 }}>
          <span aria-hidden>•</span>
          <span>{renderInline(t.slice(2))}</span>
        </div>
      );
    }
    return (
      <p key={i} style={{ fontSize: 13.5, color: 'var(--ink-500, #334)', lineHeight: 1.55, margin: '6px 0' }}>
        {renderInline(t)}
      </p>
    );
  });
}

/** Negritas **texto** → <strong>. */
function renderInline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>,
  );
}

/** Modal de solo lectura para mostrar un documento legal. */
export function LegalModal({ docId, onClose }: LegalModalProps) {
  if (!docId) return null;
  const doc = getLegalDoc(docId);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={doc.title}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '85vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid var(--ink-50, #eee)' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--navy)' }}>{doc.title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-400, #889)' }}>Actualizado: {doc.updated}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{ border: 'none', background: 'var(--ink-50, #f0f2f5)', borderRadius: 10, width: 34, height: 34, fontSize: 18, cursor: 'pointer', color: 'var(--ink-500, #334)' }}
          >×</button>
        </div>
        <div style={{ padding: '10px 18px 20px', overflowY: 'auto' }}>
          {renderBody(doc.body)}
        </div>
      </div>
    </div>
  );
}
